import React, { useState } from "react";
import { App, Form, Input, Modal } from "antd";
import { useTranslation } from "react-i18next";

interface SaveDraftDialogProps {
  open: boolean;
  /** What the campaign is already called, where anything has been typed. */
  subject?: string;
  onCancel: () => void;
  /** The draft has a name and is saved; the caller decides what happens next. */
  onSaved: (subject: string) => void;
}

/**
 * Asks what to call a draft before it is put down.
 *
 * A draft is a campaign the user will look for again in the list, and the list
 * shows it by subject — so an unnamed draft is one they cannot find. Both the
 * builder and the send step save drafts, and both ask through this rather than
 * each writing the dialog again.
 *
 * The field is a Form.Item and not a label of its own, so the space under it is
 * antd's `itemMarginBottom` (marginLG, 24) rather than a number written here.
 * One field is still a form.
 *
 * TODO(design): needs an endpoint to save a draft campaign to. Nothing leaves
 * the browser — the dialog and the message are the design's, so the flow can be
 * reviewed before it is connected. See DEVELOPER_TASKS.md.
 */
export default function SaveDraftDialog({
  open,
  subject = "",
  onCancel,
  onSaved,
}: SaveDraftDialogProps) {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const [name, setName] = useState(subject);

  // The dialog outlives one opening, so it takes the campaign's current name
  // again each time rather than whatever was typed and cancelled last. Adjusted
  // while rendering rather than in an effect: React re-renders this component
  // before it paints, where an effect would show the stale name for a frame.
  const [wasOpen, setWasOpen] = useState(open);

  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) setName(subject);
  }

  return (
    <Modal
      open={open}
      title={t("pages.campaigns.builder.saveDraftTitle")}
      okText={t("common.save")}
      cancelText={t("common.cancel")}
      okButtonProps={{ disabled: name.trim() === "" }}
      onCancel={onCancel}
      onOk={() => {
        const saved = name.trim();

        message.success(
          t("pages.campaigns.builder.draftSaved", { name: saved }),
        );
        onSaved(saved);
      }}
      destroyOnHidden
    >
      <Form layout="vertical">
        {/* Marked required, because it is: Save stays disabled until there is
            something here, and a field that refuses to go on without saying so
            first is a field that looks broken. `required` on the Form.Item is
            what draws antd's asterisk — the rule itself lives on the button,
            since the dialog holds one field and has nothing to validate on
            submit that the button has not already answered. */}
        <Form.Item label={t("pages.campaigns.table.subject")} required>
          <Input
            autoFocus
            value={name}
            placeholder={t("pages.campaigns.send.subjectHint")}
            onChange={(event) => setName(event.target.value)}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
}
